import React, { useMemo } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import type { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface InternationalPhoneInputProps {
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    defaultCountry?: string;
    showValidation?: boolean;
}

function borderErrorFromClassName(className?: string): boolean {
    if (!className) return false;
    return className.includes('border-destructive') || className.includes('border-red-500');
}

function stripBorderErrorClasses(className?: string): string | undefined {
    if (!className) return undefined;
    return className
        .replace(/\bborder-destructive\b/g, '')
        .replace(/\bborder-red-500\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

const PhoneInputField = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>(
    ({ className, ...props }, ref) => (
        <Input
            ref={ref}
            className={cn(
                'h-9 min-w-0 flex-1 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
                className
            )}
            {...props}
        />
    )
);
PhoneInputField.displayName = 'PhoneInputField';

export const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
    value = '',
    onChange,
    placeholder,
    className,
    defaultCountry = 'US',
    showValidation = true,
}) => {
    const parentBorderError = useMemo(() => borderErrorFromClassName(className), [className]);
    const wrapperClassName = useMemo(() => stripBorderErrorClasses(className), [className]);

    const validationInvalid = showValidation && Boolean(value) && !isValidPhoneNumber(value);

    const showErrorBorder = validationInvalid || parentBorderError;

    return (
        <div className={cn('relative w-full min-w-0', wrapperClassName)}>
            {/* Default countryCallingCodeEditable=true so pasted E.164 (+cc…) can switch country;
                false locks the prefix and rejects pasted numbers for other calling codes. */}
            <PhoneInput
                international
                limitMaxLength
                defaultCountry={defaultCountry as Country}
                value={value || undefined}
                onChange={val => onChange(val ?? '')}
                placeholder={placeholder}
                inputComponent={PhoneInputField}
                smartCaret
                className={cn(
                    'PhoneInput flex h-10 w-full min-w-0 items-center gap-1 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:opacity-50',
                    showErrorBorder && 'border-destructive focus-within:ring-destructive'
                )}
            />
            {showValidation && validationInvalid && (
                <div className="absolute -bottom-6 left-0 text-xs text-red-500">
                    Invalid phone number format
                </div>
            )}
        </div>
    );
};
